import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileWarning, ArrowRight, ChevronDown, CheckCircle2, AlertOctagon, XCircle, Eye, AlertTriangle } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG } from '../shared';
import type { FamilyTriageItem } from '../hooks/useGovernanceData';
interface SeedTriageCardProps {
  familyTriage: FamilyTriageItem[];
  onSelectFamily: (familyId: string) => void;
}
/**
 * SeedTriageCard — surfaces the worst-offender seed files (familyId groups)
 * so admins can prioritise cleanup file-by-file. Answers the question:
 * "Which seed has the most issues, and which should I open first?".
 *
 * Sorted by `urgencyScore` from the hook (criticals → blockers → reviews → coaches).
 * Click a row to deep-link into the Vragen tab pre-filtered to that family.
 */
export function SeedTriageCard({
  familyTriage,
  onSelectFamily
}: SeedTriageCardProps) {
  const [showAll, setShowAll] = useState(false);
  const INITIAL_LIMIT = 8;
  // Only show families that actually have something to fix
  const problematic = familyTriage.filter(f => f.criticalCount + f.blockerCount + f.reviewCount + f.warningCount > 0);
  const visible = showAll ? problematic : problematic.slice(0, INITIAL_LIMIT);
  const hiddenCount = problematic.length - INITIAL_LIMIT;
  if (problematic.length === 0) {
    return <Card className="p-5" data-id="element-4730">
        <div className="mb-4" data-id="element-4731">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4732">
            <FileWarning className="w-4 h-4 text-emerald-500" data-id="element-4733" /> Seed Prioriteit
          </h3>
          <p className="text-[11px] text-gray-500 mt-1" data-id="element-4734">
            Welk seed-bestand verdient als eerste aandacht?
          </p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-6 text-center border border-emerald-100" data-id="element-4735">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" data-id="element-4736" />
          <p className="text-sm font-medium text-emerald-800" data-id="element-4737">
            Alle seeds zijn schoon!
          </p>
          <p className="text-xs text-emerald-700 mt-1" data-id="element-4738">
            Geen enkel seed-bestand bevat afgekeurde of review-vragen.
          </p>
        </div>
      </Card>;
  }
  return <Card className="p-5" data-id="element-4739">
      <div className="mb-4 flex items-start justify-between gap-3" data-id="element-4740">
        <div data-id="element-4741">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4742">
            <FileWarning className="w-4 h-4 text-orange-500" data-id="element-4743" /> Seed Prioriteit
            <span className="text-xs font-normal text-gray-500" data-id="element-4744">
              ({problematic.length}{' '}
              {problematic.length === 1 ? 'seed' : 'seeds'} met issues)
            </span>
          </h3>
          <p className="text-[11px] text-gray-500 mt-1" data-id="element-4745">
            Gesorteerd op urgentie. Klik op een seed om alle vragen uit dat
            bestand te bekijken.
          </p>
        </div>
      </div>

      <div className="space-y-1.5" data-id="element-4746">
        <AnimatePresence initial={false} data-id="element-4747">
          {visible.map((fam, idx) => <motion.button key={fam.familyId} initial={idx >= INITIAL_LIMIT ? {
          opacity: 0,
          y: -4
        } : {
          opacity: 1,
          y: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.18,
          delay: idx * 0.015
        }} type="button" onClick={() => onSelectFamily(fam.familyId)} className="group w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 bg-white hover:border-orange-200 hover:bg-orange-50/40 transition-colors" data-id="element-4748">
              {/* Rank */}
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 group-hover:bg-orange-100 text-[10px] font-bold text-gray-500 group-hover:text-orange-700 flex items-center justify-center transition-colors" data-id="element-4749">
                {idx + 1}
              </div>

              {/* Module dot + family ID */}
              <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-4750">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${MODULE_CONFIG[fam.moduleCode]?.bgClass ?? 'bg-gray-400'}`} data-id="element-4751" />
                <div className="min-w-0 flex-1" data-id="element-4752">
                  <p className="font-mono text-xs font-semibold text-gray-900 truncate" title={fam.familyId} data-id="element-4753">
                    {fam.familyId}
                  </p>
                  <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-4754">
                    {fam.total} {fam.total === 1 ? 'vraag' : 'vragen'} ·{' '}
                    <span className="text-emerald-600 font-medium" data-id="element-4755">
                      {fam.pass} pass
                    </span>
                    {fam.fail > 0 && <span className="text-red-600 font-medium" data-id="element-4756">
                        {' '}
                        · {fam.fail} fail
                      </span>}
                    {fam.review > 0 && <span className="text-amber-600 font-medium" data-id="element-4757">
                        {' '}
                        · {fam.review} review
                      </span>}
                  </p>
                </div>
              </div>

              {/* Severity counters */}
              <div className="hidden sm:flex items-center gap-1 flex-shrink-0" data-id="element-4758">
                {fam.criticalCount > 0 && <span title="Kritiek" className="flex items-center gap-0.5 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded" data-id="element-4759">
                    <AlertOctagon className="w-3 h-3" data-id="element-4760" />
                    {fam.criticalCount}
                  </span>}
                {fam.blockerCount > 0 && <span title="Blocker" className="flex items-center gap-0.5 text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded" data-id="element-4761">
                    <XCircle className="w-3 h-3" data-id="element-4762" />
                    {fam.blockerCount}
                  </span>}
                {fam.reviewCount > 0 && <span title="Review" className="flex items-center gap-0.5 text-[10px] font-semibold text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded" data-id="element-4763">
                    <Eye className="w-3 h-3" data-id="element-4764" />
                    {fam.reviewCount}
                  </span>}
                {fam.warningCount > 0 && <span title="Coach" className="flex items-center gap-0.5 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded" data-id="element-4765">
                    <AlertTriangle className="w-3 h-3" data-id="element-4766" />
                    {fam.warningCount}
                  </span>}
              </div>

              {/* Score pill */}
              <div className="hidden md:flex flex-col items-end flex-shrink-0 w-12" data-id="element-4767">
                <span className={`text-xs font-bold tabular-nums ${fam.avgScore >= 80 ? 'text-emerald-600' : fam.avgScore >= 60 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-4768">
                  {fam.avgScore}%
                </span>
                <span className="text-[9px] text-gray-400" data-id="element-4769">gem. score</span>
              </div>

              <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-4770" />
            </motion.button>)}
        </AnimatePresence>
      </div>

      {hiddenCount > 0 && <button type="button" onClick={() => setShowAll(v => !v)} className="mt-3 w-full text-xs font-medium text-gray-600 hover:text-gray-900 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-4771">
          {showAll ? <>
              Toon top {INITIAL_LIMIT}
              <ChevronDown className="w-3 h-3 rotate-180 transition-transform" data-id="element-4772" />
            </> : <>
              Toon {hiddenCount} {hiddenCount === 1 ? 'seed' : 'seeds'} meer
              <ChevronDown className="w-3 h-3 transition-transform" data-id="element-4773" />
            </>}
        </button>}

      {/* Top issue codes for the #1 worst family — micro-diagnosis hint */}
      {visible[0]?.topIssueCodes.length > 0 && <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-4774">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5" data-id="element-4775">
            Meest voorkomende issue in #1
          </p>
          <div className="flex flex-wrap gap-1" data-id="element-4776">
            {visible[0].topIssueCodes.map(code => <span key={code} className="font-mono text-[10px] text-gray-600 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded" data-id="element-4777">
                {code}
              </span>)}
          </div>
        </div>}
    </Card>;
}